import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  ClipboardList,
  Copy,
  Loader2,
  Calendar,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  ArrowRight
} from 'lucide-react';
import { apiClient } from '../lib/apiClient';

interface ProgressSummaryData {
  summary: string;
  key_updates: string[];
  blockers: string[];
  decisions: string[];
  next_steps: string[];
  topic: string;
  since_date: string;
  generated_at: string;
}

export const ProgressTrackerPage: React.FC = () => {
  const [topic, setTopic] = useState('');
  const [sinceDate, setSinceDate] = useState(() => {
    // Default to 7 days ago
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split('T')[0];
  });
  const [isLoading, setIsLoading] = useState(false);
  const [progressData, setProgressData] = useState<ProgressSummaryData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    if (!topic.trim()) {
      setError('Please enter a topic or project name');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/api/insights/progress-summary', {
        topic: topic.trim(),
        since_date: sinceDate,
      });

      setProgressData(response.data);
    } catch (err: any) {
      console.error('Failed to generate progress summary:', err);
      setError(err.response?.data?.detail || 'Failed to generate progress summary');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (!progressData) return;
    
    let text = `Progress Summary: ${progressData.topic}\n`;
    text += `Since: ${progressData.since_date}\n\n`;
    text += `Summary: ${progressData.summary}\n\n`;
    
    if (progressData.key_updates.length > 0) {
      text += `=== KEY UPDATES ===\n`;
      progressData.key_updates.forEach((update, idx) => {
        text += `${idx + 1}. ${update}\n`;
      });
      text += '\n';
    }
    
    if (progressData.blockers.length > 0) {
      text += `=== BLOCKERS ===\n`;
      progressData.blockers.forEach((blocker, idx) => {
        text += `${idx + 1}. ${blocker}\n`;
      });
      text += '\n';
    }
    
    if (progressData.decisions.length > 0) {
      text += `=== DECISIONS MADE ===\n`;
      progressData.decisions.forEach((decision, idx) => {
        text += `${idx + 1}. ${decision}\n`;
      });
      text += '\n';
    }
    
    if (progressData.next_steps.length > 0) {
      text += `=== NEXT STEPS ===\n`;
      progressData.next_steps.forEach((step, idx) => {
        text += `${idx + 1}. ${step}\n`;
      });
    }
    
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <ClipboardList className="h-8 w-8 text-blue-500" />
            <h1 className="text-3xl font-bold text-foreground">Progress Tracker</h1>
          </div>
          <p className="text-muted-foreground">
            Track team progress on projects and initiatives by analyzing internal Slack conversations
          </p>
        </div>

        {/* Input Form */}
        <div className="bg-card border border-border rounded-lg p-6 mb-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="topic">Topic or Project Name</Label>
              <Input
                id="topic"
                placeholder="e.g., customer onboarding process"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="since-date">Since Date</Label>
              <div className="relative mt-1">
                <Calendar className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                <Input
                  id="since-date"
                  type="date"
                  value={sinceDate}
                  onChange={(e) => setSinceDate(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            {error && (
              <div className="bg-destructive/10 border border-destructive/20 text-destructive rounded-lg p-3 text-sm">
                {error}
              </div>
            )}
            <Button
              onClick={handleGenerate}
              disabled={isLoading || !topic.trim()}
              className="w-full"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating Summary...
                </>
              ) : (
                <>Generate Progress Summary</>
              )}
            </Button>
          </div>
        </div>

        {/* Results */}
        {progressData && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-foreground">{progressData.topic}</h2>
              <Button
                variant="outline"
                size="sm"
                onClick={copyToClipboard}
                className="flex items-center space-x-2"
              >
                <Copy className="h-4 w-4" />
                <span>Copy</span>
              </Button>
            </div>

            {/* Summary */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <p className="text-foreground">{progressData.summary}</p>
            </div>

            {/* Key Updates */}
            {progressData.key_updates.length > 0 && (
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <TrendingUp className="h-5 w-5 text-green-500" />
                  <h3 className="text-lg font-semibold text-foreground">Key Updates</h3>
                </div>
                <ul className="space-y-3">
                  {progressData.key_updates.map((update, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="flex-shrink-0 w-6 h-6 bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center text-sm font-medium mr-3 mt-0.5">
                        {idx + 1}
                      </span>
                      <span className="text-foreground">{update}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Blockers */}
            {progressData.blockers.length > 0 && (
              <div className="bg-card border border-red-200 dark:border-red-800 rounded-lg p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <AlertCircle className="h-5 w-5 text-red-500" />
                  <h3 className="text-lg font-semibold text-foreground">Blockers</h3>
                </div>
                <ul className="space-y-3">
                  {progressData.blockers.map((blocker, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="flex-shrink-0 w-6 h-6 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center text-sm font-medium mr-3 mt-0.5">
                        {idx + 1}
                      </span>
                      <span className="text-foreground">{blocker}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Decisions */}
            {progressData.decisions.length > 0 && (
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <CheckCircle className="h-5 w-5 text-blue-500" />
                  <h3 className="text-lg font-semibold text-foreground">Decisions Made</h3>
                </div>
                <ul className="space-y-3">
                  {progressData.decisions.map((decision, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center text-sm font-medium mr-3 mt-0.5">
                        {idx + 1}
                      </span>
                      <span className="text-foreground">{decision}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Next Steps */}
            {progressData.next_steps.length > 0 && (
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <ArrowRight className="h-5 w-5 text-purple-500" />
                  <h3 className="text-lg font-semibold text-foreground">Next Steps</h3>
                </div>
                <ul className="space-y-3">
                  {progressData.next_steps.map((step, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="flex-shrink-0 w-6 h-6 bg-purple-100 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded-full flex items-center justify-center text-sm font-medium mr-3 mt-0.5">
                        {idx + 1}
                      </span>
                      <span className="text-foreground">{step}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="text-sm text-muted-foreground text-center">
              Generated on {new Date(progressData.generated_at).toLocaleString()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgressTrackerPage;
